import React, { Fragment, PropsWithChildren, useState } from 'react';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
  Toolbar,
  SearchPanel,
} from '@devexpress/dx-react-grid-bootstrap4';

import { Asset } from '../../../types/Asset';
import LookupCell from './data_points/LookupCell';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import {
  DataTypeProvider,
  DataTypeProviderProps,
  IntegratedFiltering,
  IntegratedSorting,
  SearchState,
  SortingState,
} from '@devexpress/dx-react-grid';
import { DateTypeProvider } from 'components/DataGrid/DataTypeProviders/DateTypeProvider';
import { Info } from 'components/Icons/Info';
import { DataPoint } from 'types/DataPoint';

type Props = {
  assets: Array<Asset>;
  // eslint-disable-next-line no-unused-vars
  onAddDataPointValue: (key: string, resourceId: number, value: any) => void;
  // eslint-disable-next-line no-unused-vars
  onDeleteAssetRow: (resourceId: number) => void;
  isLoading: boolean;
  hideNoDataMessage: boolean;
};

type ColumnMap = {
  [key: string]: any;
};
const columnLabelMap: ColumnMap = {
  assetClassId: {},
  fundStructureId: {},
  investmentFocusId: {},
  geographicFocusId: {},
  currencyCode: {},
  vehicleTypeId: {},
  reportingMethodId: {},
  performanceFrequency: {},
};

const DataPoints = ({
  assets,
  onAddDataPointValue,
  onDeleteAssetRow,
  isLoading,
  hideNoDataMessage,
}: Props) => {
  const columns = [
    { name: 'name', title: 'Asset' },
    { name: 'firmName', title: 'Firm Name' },
    { name: 'productName', title: 'Product Name' },

    { name: 'assetClassId', title: 'Asset Class' },
    { name: 'fundStructureId', title: 'Fund Structure' },
    { name: 'investmentFocusId', title: 'Investment Focus' },
    { name: 'geographicFocusId', title: 'Geographic Focus' },
    { name: 'currencyCode', title: 'Base Currency' },
    { name: 'vehicleTypeId', title: 'Vehicle Type' },
    { name: 'reportingMethodId', title: 'Reporting Method' },
    { name: 'performanceFrequency', title: 'Frequency' },
    { name: 'lastSyncDate', title: 'Last Sync' },
    { name: 'actions', title: 'Actions' },
  ];

  const [defaultColumnWidths] = useState(
    columns.map((column) => ({
      columnName: column.name,
      width: column.name === 'actions' ? 90 : 180,
    })),
  );
  const [sortingStateColumnExtensions] = useState([
    { columnName: 'actions', sortingEnabled: false },
  ]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const TableComponentBase = (props: object) => <Table.Table {...props} />;

  const Cell = (props: any) => {
    const { column, row } = props;

    if (
      row.resourceId &&
      [
        'performanceFrequency',
        'assetClassId',
        'investmentFocusId',
        'currencyCode',
        'reportingMethodId',
        'fundStructureId',
        'geographicFocusId',
        'vehicleTypeId',
      ].includes(column.name)
    ) {
      return (
        <td>
          <LookupCell
            onApply={(value: any) => onAddDataPointValue(column.name, row.resourceId, value)}
            name={column.name}
            value={
              column.name === 'performanceFrequency'
                ? row.performanceFrequency || row.performanceFrequencyId
                : row[column.name]
            }
            assetClassId={row.assetClassId}
            vehicleTypeId={row.vehicleTypeId}
            mapColumnLabels={mapColumnLabels}
          />
        </td>
      );
    }
    return <Table.Cell className="" {...props} />;
  };

  /**
   * Build id to value object for use when getting cell values
   * @param columnName
   * @param dataPoints Row data object
   */
  const mapColumnLabels = (columnName: string, dataPoints: DataPoint[]) => {
    dataPoints.forEach((point) => (columnLabelMap[columnName][point.id] = point.name));
  };

  const HeaderCell = (props: any) => {
    const { column, children, ...rest } = props;
    return (
      <TableHeaderRow.Cell className={`header-${column.name}`} {...rest}>
        {children}
        {column.name === 'reportingMethodId' && <Info size="sm" className="blueInfo" />}
      </TableHeaderRow.Cell>
    );
  };

  type DataTypeFormatterComponent = React.FC<DataTypeProvider.ValueFormatterProps>;
  type AssetValueFormatter = Omit<DataTypeProvider.ValueFormatterProps, 'row'> & {
    row: Asset;
  };

  const ActionsFormatter: React.FC<AssetValueFormatter> = ({ row }: AssetValueFormatter) => (
    <Fragment>
      <button className="btn btn-link p-0" onClick={() => onDeleteAssetRow(row.resourceId!)}>
        Delete
      </button>
    </Fragment>
  );

  const TextInputFormatter: React.FC<AssetValueFormatter> = (props: AssetValueFormatter) => {
    const {
      column: { name: propertyName },
      row: asset,
    } = props;

    const [value, setValue] = React.useState<string>((asset as Record<string, any>)[propertyName]);

    React.useEffect(() => {
      (asset as Record<string, any>)[propertyName] = value;
    }, [asset, propertyName, value]);

    if (!asset.resourceId) {
      return null;
    }

    return (
      <input
        className="form-control form-control-md"
        type="text"
        value={value}
        onBlur={() => {
          onAddDataPointValue(propertyName, asset.resourceId!, { value });
        }}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        autoComplete="off"
      />
    );
  };

  const ActionsTypeProvider: React.FC<DataTypeProviderProps> = (props) => (
    <DataTypeProvider
      formatterComponent={ActionsFormatter as DataTypeFormatterComponent}
      {...props}
    />
  );

  const TextInputProvider: React.FC<DataTypeProviderProps> = (props) => (
    <DataTypeProvider
      formatterComponent={TextInputFormatter as DataTypeFormatterComponent}
      {...props}
    />
  );

  // Nb: columnName actually corresponds to property name
  const cellValue = (row: any, columnName: string) => {
    if (!columnLabelMap[columnName]) {
      return row[columnName];
    }
    return columnLabelMap[columnName][row[columnName]];
  };

  return (
    <Grid
      rows={assets || []}
      columns={columns}
      getRowId={(asset) => asset.resourceId}
      getCellValue={cellValue}>
      <TextInputProvider for={['firmName', 'productName']} />
      <DateTypeProvider for={['lastSyncDate']} />
      <ActionsTypeProvider for={['actions']} />

      <Table
        cellComponent={Cell}
        tableComponent={TableComponentBase}
        noDataCellComponent={(props: PropsWithChildren<Table.NoDataCellProps>) =>
          hideNoDataMessage ? null : (
            <td className="p-5 text-center" colSpan={props.colSpan}>
              No Data
            </td>
          )
        }
      />
      <SortingState
        columnExtensions={sortingStateColumnExtensions}
        defaultSorting={[{ columnName: 'name', direction: 'asc' }]}
      />
      <IntegratedSorting />
      <SearchState />
      <IntegratedFiltering />

      <Table cellComponent={Cell} tableComponent={TableComponentBase} />
      <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
      <TableHeaderRow cellComponent={HeaderCell} showSortingControls />
      <Toolbar />
      <SearchPanel />
    </Grid>
  );
};
export default DataPoints;
